import { createSelector, createSlice, PayloadAction, SerializedError } from "@reduxjs/toolkit";
import type { Draft } from "immer";
import PaginatedResponse from "_shared/_api/pagination/responses/paginated-response";
import { RootState } from "_shared/_stores/redux-store";
import Guid from "_shared/guids/guid";
import WorkAgreementQuerySchema, { WorkAgreementQueryJSON } from "work/work-agreements/_api/queries/_schemas/work-agreement-query-schema";
import WorkAgreementQuery from "work/work-agreements/_api/queries/work-agreement-query";
import BarebonesWorkAgreementResponse from "work/work-agreements/_api/responses/barebones-work-agreement-response";
import { workAgreementsApi } from "work/work-agreements/_api/work-agreement-api";
import { validateJSON, WorkAgreementJSON } from "work/work-agreements/_schemas/work-agreement-schema";
import WorkAgreement from "work/work-agreements/work-agreement";
import { cleanupWorkDocumentWrappers } from "work/work-document-cache-cleanup";

export type WorkAgreementLoadIssue = { id: Guid; issue: string };
export const isWorkAgreementLoadIssue = (value: object): value is WorkAgreementLoadIssue => {
  return "issue" in value;
};
type WorkAgreementStoreState = {
  byId: Record<string, WorkAgreementJSON | null | undefined>;
  bareBones: Record<string, BarebonesWorkAgreementResponse | undefined>;
  byQuery: Record<string, PaginatedResponse<WorkAgreementJSON | WorkAgreementLoadIssue>>;
  interfaceQueries: {
    entries: Record<string, WorkAgreementQueryJSON>;
    lastSuccessfulResponses: Record<string, PaginatedResponse<WorkAgreementJSON | WorkAgreementLoadIssue>>;
  };
};
const initialState: WorkAgreementStoreState = {
  byId: {},
  bareBones: {},
  byQuery: {},
  interfaceQueries: {
    entries: {},
    lastSuccessfulResponses: {},
  },
};
type DraftPaginatedWorkAgreementResponse = Draft<PaginatedResponse<WorkAgreementJSON | WorkAgreementLoadIssue>>;

const cleanupWorkAgreementJSONForDeletedDocument = (
  workAgreementJSON: WorkAgreementJSON | undefined | null,
  documentId: string,
) => {
  if (!workAgreementJSON) return false;
  return cleanupWorkDocumentWrappers(
    workAgreementJSON,
    documentId,
    workAgreementJSON.hireDate ? "markUnavailable" : "remove",
  );
};

const cloneWorkAgreementDocumentWrapper = (
  wrapper: WorkAgreementJSON["clientPolicies"],
): WorkAgreementJSON["clientPolicies"] =>
  wrapper
    ? {
        ...wrapper,
        documents: wrapper.documents?.map((document) => ({ ...document })),
      }
    : wrapper;

const cloneWorkAgreementJSON = (workAgreementJSON: WorkAgreementJSON): WorkAgreementJSON => ({
  ...workAgreementJSON,
  clientPolicies: cloneWorkAgreementDocumentWrapper(workAgreementJSON.clientPolicies),
  vendorPolicies: cloneWorkAgreementDocumentWrapper(workAgreementJSON.vendorPolicies),
  conflicts: cloneWorkAgreementDocumentWrapper(workAgreementJSON.conflicts),
});

const cleanupWorkAgreementResponseForDeletedDocument = (
  response: PaginatedResponse<WorkAgreementJSON | WorkAgreementLoadIssue> | DraftPaginatedWorkAgreementResponse,
  documentId: string,
) => {
  let changed = false;
  const data = response.data.map((workAgreementJSON) => {
    if (isWorkAgreementLoadIssue(workAgreementJSON)) return workAgreementJSON;
    const clonedWorkAgreementJSON = cloneWorkAgreementJSON(workAgreementJSON);
    if (!cleanupWorkAgreementJSONForDeletedDocument(clonedWorkAgreementJSON, documentId)) return workAgreementJSON;
    changed = true;
    return clonedWorkAgreementJSON;
  });

  return changed ? PaginatedResponse.from(data, response.headers) : response;
};

const invalidateWorkAgreementListCaches = (state: WorkAgreementStoreState | Draft<WorkAgreementStoreState>) => {
  state.byQuery = {};
  state.interfaceQueries.lastSuccessfulResponses = {};
};

const workAgreementsSlice = createSlice({
  name: "workAgreements",
  initialState,
  reducers: {
    addWorkAgreement: (state, action: PayloadAction<WorkAgreement>) => {
      const workAgreement = action.payload;
      if (!workAgreement.id) {
        console.error("Work agreement does not have an id");
        return;
      }

      state.byId[workAgreement.id.value] = workAgreement.toJSON();
      invalidateWorkAgreementListCaches(state);
    },
    updateWorkAgreement: (state, action: PayloadAction<WorkAgreementJSON>) => {
      const workAgreementJSON = action.payload;
      if (!workAgreementJSON.id) {
        console.error("Work agreement does not have an id", workAgreementJSON);
        return;
      }

      state.byId[workAgreementJSON.id] = workAgreementJSON;
      invalidateWorkAgreementListCaches(state);
    },
    removeWorkAgreement: (state, action: PayloadAction<Guid>) => {
      state.byId[action.payload.value] = null;
      invalidateWorkAgreementListCaches(state);
    },
    updateWorkAgreementInterfaceQuery: (
      state,
      action: PayloadAction<{
        interfaceName: string;
        query: WorkAgreementQuery;
      }>,
    ) => {
      const { interfaceName, query } = action.payload;
      state.interfaceQueries.entries[interfaceName] = WorkAgreementQuerySchema.serialize(query);
    },
    cleanupDeletedDocumentReferences: (state, action: PayloadAction<Guid>) => {
      const documentId = action.payload.value;

      for (const workAgreementJSON of Object.values(state.byId)) {
        cleanupWorkAgreementJSONForDeletedDocument(workAgreementJSON, documentId);
      }

      for (const [queryKey, response] of Object.entries(state.byQuery)) {
        state.byQuery[queryKey] = cleanupWorkAgreementResponseForDeletedDocument(response, documentId);
      }

      for (const [interfaceName, response] of Object.entries(state.interfaceQueries.lastSuccessfulResponses)) {
        state.interfaceQueries.lastSuccessfulResponses[interfaceName] = cleanupWorkAgreementResponseForDeletedDocument(
          response,
          documentId,
        );
      }
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(workAgreementsApi.endpoints.getWorkAgreementById.matchFulfilled, (state, action) => {
      const workAgreementJSON = action.payload;
      if (workAgreementJSON === null) {
        state.byId[action.meta.arg.originalArgs.workAgreementId] = null;
        return;
      }
      if (!workAgreementJSON?.id) {
        console.error("Work agreement does not have an id", workAgreementJSON);
        return;
      }
      state.byId[workAgreementJSON.id] = workAgreementJSON;
    });

    builder.addMatcher(workAgreementsApi.endpoints.getWorkAgreementsByIds.matchFulfilled, (state, action) => {
      const workAgreements = action.payload;
      workAgreements.forEach((workAgreement) => {
        if (!workAgreement.id) {
          console.error("Work agreement does not have an id", workAgreement);
          return;
        }
        state.bareBones[workAgreement.id] = workAgreement;
      });
    });

    builder.addMatcher(workAgreementsApi.endpoints.getWorkAgreements.matchFulfilled, (state, action) => {
      if (!action.meta.arg.originalArgs) return;
      const { query, interfaceName } = action.meta.arg.originalArgs;
      const responseJson = action.payload;
      if (!query || !responseJson) return;
      const queryKey = query.cacheKey;

      const validatedWorkAgreementData: (WorkAgreementJSON | WorkAgreementLoadIssue)[] = [];

      for (const data of responseJson.data) {
        const workAgreementJSON: WorkAgreementJSON = data;

        let id = Guid.EMPTY;
        try {
          id = Guid.fromJSON(workAgreementJSON.id) ?? id;
          if (validateJSON(workAgreementJSON)) {
            validatedWorkAgreementData.push(workAgreementJSON);
          }
        } catch (error) {
          validatedWorkAgreementData.push({
            id,
            issue: "Error loading work agreement.",
          });
          console.error("Error validating work agreement JSON:", error);
        }
      }

      const paginatedResponse = PaginatedResponse.from<WorkAgreementJSON | WorkAgreementLoadIssue>(
        validatedWorkAgreementData,
        responseJson.headers,
      );

      state.byQuery[queryKey] = paginatedResponse;
      if (interfaceName) {
        // Update last successful response for this interface
        state.interfaceQueries.lastSuccessfulResponses[interfaceName] = paginatedResponse;
      }

      for (const workAgreementData of paginatedResponse.data) {
        if (!workAgreementData.id) {
          console.error("Failed to deserialize incoming work agreement", workAgreementData);
          continue;
        }
        if (isWorkAgreementLoadIssue(workAgreementData)) continue;

        const incomingWorkAgreement: WorkAgreement | null | undefined = WorkAgreement.fromJSON(workAgreementData);
        if (!incomingWorkAgreement?.id) {
          console.error("Failed to deserialize incoming work agreement", workAgreementData);
          continue;
        }

        state.byId[incomingWorkAgreement.id.value] = workAgreementData;
      }
    });

    builder.addMatcher(workAgreementsApi.endpoints.endWorkAgreement.matchFulfilled, (state, action) => {
      const workAgreementJSON = action.payload;
      if (!workAgreementJSON.id) {
        console.error("Work agreement does not have an id", workAgreementJSON);
        return;
      }
      state.byId[workAgreementJSON.id] = workAgreementJSON;
      invalidateWorkAgreementListCaches(state);
    });

    builder.addMatcher(workAgreementsApi.endpoints.quitWorkTeam.matchFulfilled, (state, action) => {
      const workAgreementIdValue = action.meta.arg.originalArgs.workId;
      if (!workAgreementIdValue) {
        console.error("Work agreement does not have an id", workAgreementIdValue);
        return;
      }
      state.byId[workAgreementIdValue] = null;
      invalidateWorkAgreementListCaches(state);
    });
  }
});

export const { 
  addWorkAgreement, 
  updateWorkAgreement,
  removeWorkAgreement,
  updateWorkAgreementInterfaceQuery,
  cleanupDeletedDocumentReferences,
} = workAgreementsSlice.actions;

const selectWorkAgreementById = createSelector(
  [(state: RootState) => state.workAgreements.byId, (_: RootState, id?: Guid) => id],
  (entries, id) => {
    if (!id) return undefined;
    const entry = entries[id.value];
    if (!entry) return undefined;
    return entry;
  }
);
export const getWorkAgreementById = (id: Guid | undefined) => (state: RootState) => selectWorkAgreementById(state, id);

const selectWorkAgreementQueryByInterface = createSelector(
  [
    (state: { workAgreements: WorkAgreementStoreState }) => state.workAgreements.interfaceQueries.entries,
    (_: { workAgreements: WorkAgreementStoreState }, interfaceName: string) => interfaceName,
  ],
  (queries, interfaceName) => {
    const queryJson = queries[interfaceName];
    if (!queryJson) return;
    return WorkAgreementQuery.fromJSON(queryJson);
  },
);
export const getWorkAgreementQueryByInterface = (interfaceName: string) => (state: { workAgreements: WorkAgreementStoreState }) =>
  selectWorkAgreementQueryByInterface(state, interfaceName);

const selectWorkAgreementsByQueryKey = createSelector(
  [
    (state: { workAgreements: WorkAgreementStoreState }, queryKey: string | undefined | null) => state.workAgreements.byQuery[queryKey ?? ""],
    (_state: { workAgreements: WorkAgreementStoreState }, _queryKey: string | undefined | null, query: WorkAgreementQuery | undefined | null) =>
      query,
  ],
  (
    paginatedWorkAgreementJSONEntry: PaginatedResponse<WorkAgreementJSON | WorkAgreementLoadIssue> | undefined,
  ): PaginatedResponse<WorkAgreementJSON | WorkAgreementLoadIssue> | undefined => {
    if (!paginatedWorkAgreementJSONEntry) return;
    return paginatedWorkAgreementJSONEntry;
  },
);

export const getWorkAgreementsByQuery = (query: WorkAgreementQuery | undefined | null) => (state: { workAgreements: WorkAgreementStoreState }) =>
  selectWorkAgreementsByQueryKey(state, query?.cacheKey, query);

const selectLastSuccessfulWorkAgreementQueryByInterface = createSelector(
  [
    (state: { workAgreements: WorkAgreementStoreState }, interfaceName: string) =>
      state.workAgreements.interfaceQueries.lastSuccessfulResponses[interfaceName],
    (_state: { workAgreements: WorkAgreementStoreState }, _interfaceName: string) => undefined,
  ],
  (
    paginatedWorkAgreementJSONEntry: PaginatedResponse<WorkAgreementJSON | WorkAgreementLoadIssue> | undefined,
  ): PaginatedResponse<WorkAgreementJSON | WorkAgreementLoadIssue> | undefined => {
    if (!paginatedWorkAgreementJSONEntry) return;
    return paginatedWorkAgreementJSONEntry;
  },
);
export const getLastSuccessfulWorkAgreementQueryByInterface =
  (interfaceName: string) => (state: { workAgreements: WorkAgreementStoreState }) => {
    return selectLastSuccessfulWorkAgreementQueryByInterface(state, interfaceName);
  };

const selectWorkAgreementsByIds = createSelector(
  [(state: RootState) => state.workAgreements.bareBones, (_: RootState, ids: Guid[]) => ids],
  (entries, ids) => {
    return ids.map((id) => entries[id.value]).filter((workAgreement) => workAgreement !== undefined);
  }
);
export const getWorkAgreementsByIds = (ids: Guid[]) => (state: RootState) => selectWorkAgreementsByIds(state, ids);

export type WorkAgreementError = {
  id: Guid;
  error: SerializedError;
};

export default workAgreementsSlice;
